import {
  CreateFilterOptionsConfig,
  UseAutocompleteProps,
} from "@mui/material/useAutocomplete";
import {
  SearchListItem,
  SearchTagKey,
  SearchTagSettings,
} from "../types/search";

interface CustomSearchFilterOptions
  extends CreateFilterOptionsConfig<SearchListItem> {
  tagSettings?: SearchTagSettings;
}

// https://stackoverflow.com/questions/990904/remove-accents-diacritics-in-a-string-in-javascript
// Give up on IE 11 support for this feature
function stripDiacritics(string: string) {
  return typeof string.normalize !== "undefined"
    ? string.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    : string;
}

export function createCustomFilterOptions({
  ignoreAccents = true,
  ignoreCase = true,
  limit,
  matchFrom = "any",
  trim = false,
  tagSettings = {
    destination: false,
    port: false,
    vesselType: false,
  },
}: CustomSearchFilterOptions = {}): UseAutocompleteProps<
  SearchListItem,
  undefined,
  undefined,
  undefined
>["filterOptions"] {
  return (options, { inputValue }) => {
    let input = trim ? inputValue.trim() : inputValue;
    if (ignoreCase) {
      input = input.toLowerCase();
    }
    if (ignoreAccents) {
      input = stripDiacritics(input);
    }

    if (!input) {
      return typeof limit === "number" ? options.slice(0, limit) : options;
    }

    const filteredOptions = options.filter((option) => {
      //   let candidate = (stringify || getOptionLabel)(option);
      const { name, description, tags } = option;
      const candidates = [name];
      if (description) {
        candidates.push(description);
      }
      if (tags) {
        const keys = Object.keys(tags) as Array<SearchTagKey>;
        keys.forEach((k) => {
          const val = tags[k]?.value;
          if (!val || !tagSettings[k]) return;
          candidates.push(val);
        });
      }

      let matches = false;
      candidates.forEach((c) => {
        let normalized = c;
        if (ignoreCase) {
          normalized = normalized.toLowerCase();
        }
        if (ignoreAccents) {
          normalized = stripDiacritics(normalized);
        }

        if (!matches) {
          matches =
            matchFrom === "start"
              ? normalized.indexOf(input) === 0
              : normalized.indexOf(input) > -1;
        }
      });
      return matches;
    });

    return typeof limit === "number"
      ? filteredOptions.slice(0, limit)
      : filteredOptions;
  };
}
